/**
 * Stored source documents (BB-FILE-DROP-01, contract Appendix B.2 `GET /files`)
 * — feeds the source-doc library. Read-only TanStack query, same key convention
 * as useBbExtractions.
 *
 * `reader_enabled` rides on the same response by contract, so the library can
 * say "reader not enabled here" without firing a doomed POST /read to find out.
 */
import { useQuery } from '@tanstack/react-query';
import { bbFilesService, type BbFileRow, type BbFileStatus } from '@/services/bbFilesService';
import type { SourceDoc, SourceDocStatus } from '@/components/brand-bible/SourceDocLibrary';

export const bbFilesKeys = {
  list: (tenantId: string) => ['bb-files', tenantId] as const,
};

/** bb_files.status (DB vocabulary) → the library's presentation state. */
const STATUS_MAP: Record<BbFileStatus, SourceDocStatus> = {
  stored: 'stored',
  parsing: 'reading',
  parsed: 'done',
  failed: 'error',
};

export function toSourceDoc(row: BbFileRow, draftCount?: number): SourceDoc {
  return {
    id: row.id,
    name: row.filename,
    mime: row.mime,
    size: row.bytes_size,
    uploadedAt: row.created_at,
    status: STATUS_MAP[row.status] ?? 'queued',
    // Only meaningful once parsed; undefined keeps the library from claiming
    // "0 drafts" for a file that has not been read yet.
    draftCount: row.status === 'parsed' ? draftCount : undefined,
    error: row.parse_error ?? undefined,
  };
}

export interface BbFilesView {
  docs: SourceDoc[];
  readerEnabled: boolean | undefined;
}

export function useBbFiles(tenantId: string | null, draftCounts?: Map<string, number>) {
  return useQuery({
    queryKey: bbFilesKeys.list(tenantId ?? ''),
    queryFn: async (): Promise<BbFilesView> => {
      if (!tenantId) return { docs: [], readerEnabled: undefined };
      const res = await bbFilesService.listFiles(tenantId);
      const docs = (res.files ?? [])
        // Soft-deleted rows stay in the table; the library shows live files only.
        .filter((f) => f.deleted_at == null)
        .map((f) => toSourceDoc(f, draftCounts?.get(f.id)));
      return { docs, readerEnabled: res.reader_enabled };
    },
    enabled: Boolean(tenantId),
  });
}
